import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { NativeNavigation } from "@eventr/native/navigation";
import { useNativeNavigationViewContext } from "@eventr/native/navigation/react";
import { isIosNativeNavigation } from "feat/nativeNavigation/platform";
import { flushPendingAuthLink } from "../deeplink/auth-deeplink";
import { flushPendingDeeplink, ingestLaunchLocation } from "../deeplink/pendingDeeplink";
import { flushPendingGuestLink } from "../guest/pendingGuestLink";
import { NavigationCommitProbe } from "./navigationCommit";
import { isChangingLanguage } from "./navigationGuards";
import { performPush, type PushPathOptions } from "./performPush";
import { performReplace } from "./performReplace";
import { queueViewTransition } from "./viewTransitionQueue";

/**
 * The window.* navigation globals belong to the view on screen. Under iOS native navigation a view
 * claims them on its viewDidAppear; web and Android claim them on mount, where there is one router.
 */
export const GlobalRouter = (props: { children: React.ReactNode }) => {
  const nav = useNavigate();
  const { addViewDidAppearListener, componentId } = useNativeNavigationViewContext();

  const claim = useCallback(() => {
    window.pushPath = async (path: string, options?: PushPathOptions) => {
      await performPush(nav, path, options);
    };

    window.replacePath = async (path: string) => {
      await performReplace(nav, path);
    };

    window.goBack = async (useRight?: boolean) => {
      if (isChangingLanguage()) return;
      let animOption = "left";

      if (useRight) animOption = "right";

      await queueViewTransition(async () => {
        nav(-1);
      }, ["slide", animOption]);
    };

    flushPendingDeeplink();
    flushPendingAuthLink();
    flushPendingGuestLink();
  }, [nav]);

  useEffect(() => {
    // One-shot per session; on the web this is the only path that feeds an org-carrying launch
    // URL through the resolver.
    ingestLaunchLocation();

    if (!isIosNativeNavigation()) claim();

    const removeListener = addViewDidAppearListener(claim);

    // The listener attaches asynchronously, and a root presented unanimated (a cold launch
    // straight onto a link's screen) can appear before it does. Missing that appearance left
    // no view holding the globals, so the first push threw. Claim now if already on screen.
    let cancelled = false;
    if (isIosNativeNavigation() && componentId) {
      void NativeNavigation.get()
        .then(({ component }) => {
          if (!cancelled && component?.id === componentId) claim();
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
      removeListener();
    };
  }, [claim, addViewDidAppearListener, componentId]);

  return (
    <>
      <NavigationCommitProbe />
      {props.children}
    </>
  );
};
